<template>
    <div class="top-page">
        <div class="top-page__container">
            <div class="top-page__breadcrumbs breadcrumbs">
                <ul class="breadcrumbs__list">
                    <li class="breadcrumbs__item"><router-link to="/" class="breadcrumbs__link">Le'Perle</router-link></li>
                    <!-- Отображение breadcrumbs из categoryData -->
                    <li class="breadcrumbs__item" v-for="(item, index) in breadcrumbs" :key="index">
                        <router-link v-if="item.slug" :to="{ name: 'catalog-categoryId', params: { slug: item.slug } }" class="breadcrumbs__link">{{ item.title }}</router-link>
                        <span v-else class="breadcrumbs__current">{{ item.title }}</span>
                    </li>
                </ul>
            </div>
            <div class="top-page__body">
                <!-- Отображение заголовка из categoryData -->
                <h1 class="top-page__title" :data-title="currentCategoryTitleEn">{{ currentCategoryTitle }}</h1>

                <div class="top-page__control">
                    <!-- Передача categoryId из categoryData -->
                    <Sorting @sortingChanged="handleSortingChange" :categoryId="categoryId" />

                    <button class="top-page__button top-page__button_hide _icon-filter" @click="toggleOpenFilter">Фільтри</button>
                </div>
            </div>
        </div>
    </div>

    <section class="catalog">
        <div class="catalog__container">
            <div class="catalog__body">
                <!-- Передача categoryId из categoryData -->
                <Filter v-if="categoryId > 0" class="catalog__filter" :categoryId="categoryId" :isFilterOpen="isFilterOpen" :closeFilter="toggleOpenFilter" @filterChange="handleFilterChange" @priceChange="handlePriceChange" @inStockChange="handleStockChange" @availableForOrderChange="handleAvailableForOrderChange" />
                <div class="catalog__content">
                    <!-- Отображение продуктов из productsData -->
                    <div class="catalog__items product-items">
                        <ProductCart v-for="(product, index) in products" :key="product.id" :product="product" @updateShowVariants="updateShowVariants(index, $event)" />
                    </div>
                    <div class="more">
                        <div class="more__pagination pagination-more">
                            <!-- Пагинация на основе productsData.meta -->
                            <a href="#" class="pagination-more__item" v-for="page in paginate()" :key="page" :class="{ active: currentPage === page }" @click.prevent="page !== '...' && handlePageChange(page)">
                                {{ page }}
                            </a>
                        </div>

                        <div class="more__body">
                            <!-- Кнопка "Показати більше" на основе productsData.meta -->
                            <button class="more__button button" v-if="currentPage < totalPages" @click="loadMore">Показати більше</button>

                            <div class="more__view">
                                переглянуто
                                <!-- Отображение количества продуктов -->
                                <b>{{ products.length }}</b>
                                товарів з
                                <!-- Отображение общего количества из productsData.meta -->
                                <b>{{ totalProducts }}</b>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <ProductSlider :title="'Інші клієнти також купили'" :products="viewedProducts" id="1" />
    <ProductSlider :title="'Останні переглянуті товари'" :products="viewedProducts" id="2" />
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRouter, useRoute, useAsyncData } from "#imports"; // Используем автоимпорты Nuxt
import { useToast } from "vue-toast-notification";
import { Filter, Sorting, ProductCart, ProductSlider } from "#components";
import "vue-toast-notification/dist/theme-bootstrap.css";

const props = defineProps({
    categoryId: String,
    subCategorySlug: {
        type: String,
        default: null,
    },
});

const config = useRuntimeConfig();
const apiUrl = config.public.apiUrl;
const toast = useToast();
const router = useRouter();
const route = useRoute();
const nuxtApp = useNuxtApp();
const { $getCookie, $fbq, $getEventId } = useNuxtApp(); // Получаем $getEventId

const isFilterOpen = ref(false);
const products = ref([]); // Теперь будет наполняться из useAsyncData и loadMore
const showVariantsStates = ref([]);
const breadcrumbs = ref([]);
const currentCategoryTitle = ref("");
const currentCategoryTitleEn = ref("");
const categoryId = ref(0); // Будет установлено из categoryData
const currentPage = ref(route.query.page ? parseInt(route.query.page) : 1); // Инициализация из route.query
const totalPages = ref(0); // Будет установлено из productsData
const totalProducts = ref(0); // Будет установлено из productsData
const viewedProducts = ref([]);
const isLoadingMore = ref(false); // Флаг для предотвращения двойной загрузки

// --- Получение данных категории с помощью useAsyncData ---
const categoryKey = computed(() => `category-${props.categoryId}-${props.subCategorySlug}`);
const { data: categoryData, error: categoryError } = await useAsyncData(
    categoryKey.value,
    async () => {
        const subCategory = props.subCategorySlug ? `/${props.subCategorySlug}` : "";
        try {
            const response = await $fetch(`${apiUrl}categories/${props.categoryId}${subCategory}`);
            return response.data;
        } catch (err) {
            console.error("Error fetching category:", err);
            if (err.response && err.response.status === 404) {
                // В Nuxt 3 используем showError для отображения страницы ошибки
                showError({ statusCode: 404, statusMessage: "Page Not Found" });
            }
            return null; // Возвращаем null в случае ошибки
        }
    },
    { watch: [() => props.categoryId, () => props.subCategorySlug] } // Перезагрузка при изменении props
);

// Обновление состояния компонента при получении данных категории
watch(
    categoryData,
    (newCategory) => {
        if (newCategory) {
            breadcrumbs.value = [];
            const parent = newCategory.parent ? newCategory.parent.title + " – " : "";
            if (newCategory.parent) {
                breadcrumbs.value.push({
                    title: newCategory.parent.title,
                    slug: newCategory.parent.slug,
                });
            }
            breadcrumbs.value.push({
                title: newCategory.title,
                slug: "", // Текущая категория не имеет slug в breadcrumbs
            });
            categoryId.value = newCategory.id;
            currentCategoryTitle.value = parent + newCategory.title;
            currentCategoryTitleEn.value = newCategory.title_en;
        } else {
            // Сброс состояния, если категория не найдена
            breadcrumbs.value = [];
            categoryId.value = 0;
            currentCategoryTitle.value = "";
            currentCategoryTitleEn.value = "";
        }
    },
    { immediate: true }
); // immediate: true для инициализации при первой загрузке

// --- Получение данных продуктов с помощью useAsyncData ---
const productsKey = computed(() => `products-${categoryId.value}-${JSON.stringify(route.query)}`);
const {
    data: productsData,
    error: productsError,
    pending: productsPending,
    refresh: refreshProducts,
} = await useAsyncData(
    productsKey.value, // Ключ зависит от categoryId и параметров запроса
    async () => {
        if (categoryId.value === 0) return { data: [], meta: { current_page: 1, last_page: 1, total: 0 } }; // Не делаем запрос, если нет ID категории

        const filtersQuery = route.query.filters; // Берем из route.query
        const prices = route.query.prices ? JSON.parse(route.query.prices) : { minPrice: 0, maxPrice: 10000000 };
        const sorting = route.query.sorting || (categoryId.value === 2 ? "latest" : "default");
        const inStock = route.query.inStock;
        const availableForOrderChange = route.query.ailableForOrderChange;
        const page = route.query.page || 1; // Берем страницу из route.query

        try {
            const response = await $fetch(`${apiUrl}products`, {
                params: {
                    category_id: categoryId.value,
                    filters: filtersQuery, // Передаем как есть (строка JSON или объект)
                    min_price: prices.minPrice,
                    max_price: prices.maxPrice,
                    in_stock: inStock,
                    available_for_order: availableForOrderChange,
                    page: page,
                    sorting: sorting,
                },
            });
            return response; // Возвращаем весь ответ (data и meta)
        } catch (err) {
            console.error("Error fetching products:", err);
            return { data: [], meta: { current_page: 1, last_page: 1, total: 0 } }; // Возвращаем пустой результат в случае ошибки
        }
    },
    {
        watch: [categoryId, () => route.query], // Перезагрузка при изменении categoryId или route.query
        // initialCache: false // Раскомментируйте, если не хотите кешировать начальный результат
    }
);

// Обновление состояния компонента при получении данных продуктов
watch(
    productsData,
    (newProductsData) => {
        let productsChanged = false; // Флаг для отслеживания реального изменения данных
        if (newProductsData && !isLoadingMore.value) {
            const oldProductsJson = JSON.stringify(products.value); // Сравним JSON для надежности
            const newProductsJson = JSON.stringify(newProductsData.data || []);

            if (oldProductsJson !== newProductsJson) {
                products.value = newProductsData.data || [];
                productsChanged = true; // Данные действительно изменились
            }

            currentPage.value = newProductsData.meta.current_page || 1;
            totalPages.value = newProductsData.meta.last_page || 1;
            totalProducts.value = newProductsData.meta.total || 0;
            // Сброс состояний вариантов только если продукты изменились
            if (productsChanged) {
                showVariantsStates.value = new Array(products.value.length).fill(false);
            }
        }
        // Сбрасываем флаг после обработки данных (даже если это не loadMore)
        isLoadingMore.value = false;

        // --- Вызов FB Pixel ---
        // Выполняем на клиенте, после обновления данных и если данные реально изменились или были загружены впервые
        if (process.client && productsChanged && products.value.length > 0 && currentCategoryTitle.value) {
            nextTick(() => {
                fbCatalog();
            });
        }
    },
    { immediate: true } // immediate: true нужен для инициализации и первого вызова fbCatalog на клиенте
);

// --- Обработчики ---

const countShowVariants = computed(() => {
    return showVariantsStates.value.filter((state) => state).length;
});

const toggleOpenFilter = () => {
    isFilterOpen.value = !isFilterOpen.value;
};

// Обновляем параметры маршрута, useAsyncData среагирует
const updateRouteQuery = (newQuery) => {
    router.push({ query: { ...route.query, ...newQuery, page: 1 } }); // Сбрасываем на 1 страницу при фильтрации/сортировке
};

const handleFilterChange = (filter) => {
    const currentFilters = route.query.filters ? JSON.parse(route.query.filters) : {};
    if (filter.checked) {
        if (!currentFilters[filter.id]) {
            currentFilters[filter.id] = [];
        }
        if (!currentFilters[filter.id].includes(filter.value)) {
            currentFilters[filter.id].push(filter.value);
        }
    } else {
        if (currentFilters[filter.id]) {
            const index = currentFilters[filter.id].indexOf(filter.value);
            if (index > -1) {
                currentFilters[filter.id].splice(index, 1);
            }
            if (currentFilters[filter.id].length === 0) {
                delete currentFilters[filter.id];
            }
        }
    }
    updateRouteQuery({ filters: JSON.stringify(currentFilters) });
};

const handlePriceChange = (prices) => {
    updateRouteQuery({ prices: JSON.stringify(prices) });
};

const handleSortingChange = (sorting) => {
    updateRouteQuery({ sorting });
};

const handleStockChange = (inStock) => {
    updateRouteQuery({ inStock: inStock });
};

const handleAvailableForOrderChange = (availableForOrderChange) => {
    updateRouteQuery({ ailableForOrderChange: availableForOrderChange });
};

const handlePageChange = (page) => {
    if (page !== currentPage.value) {
        router.push({ query: { ...route.query, page: page } });
    }
};

// --- Загрузка дополнительных продуктов (клиентская) ---
const loadMore = async () => {
    if (isLoadingMore.value || currentPage.value >= totalPages.value) return; // Предотвращаем повторный запуск

    isLoadingMore.value = true;
    const nextPage = currentPage.value + 1;

    // Используем параметры из текущего route.query
    const filtersQuery = route.query.filters;
    const prices = route.query.prices ? JSON.parse(route.query.prices) : {};
    const sorting = route.query.sorting || (categoryId.value === 2 ? "latest" : "default");
    const inStock = route.query.inStock;
    const availableForOrderChange = route.query.ailableForOrderChange;

    try {
        const response = await $fetch(`${apiUrl}products`, {
            params: {
                category_id: categoryId.value,
                filters: filtersQuery,
                min_price: prices.minPrice,
                max_price: prices.maxPrice,
                in_stock: inStock,
                available_for_order: availableForOrderChange,
                page: nextPage, // Запрашиваем следующую страницу
                sorting: sorting,
            },
        });

        if (response.data && response.data.length > 0) {
            products.value.push(...response.data); // Добавляем новые продукты
            currentPage.value = response.meta.current_page; // Обновляем текущую страницу
            // Обновляем showVariantsStates для новых продуктов
            showVariantsStates.value = showVariantsStates.value.concat(new Array(response.data.length).fill(false));
        }
        // Обновляем totalPages и totalProducts на всякий случай, если они изменились
        totalPages.value = response.meta.last_page;
        totalProducts.value = response.meta.total;

        // Обновляем URL без перезагрузки страницы (опционально)
        router.replace({ query: { ...route.query, page: currentPage.value } });
    } catch (error) {
        console.error("Error loading more products:", error);
        toast.error("Помилка завантаження товарів");
    } finally {
        // isLoadingMore.value = false; // Сбрасываем флаг в watch(productsData)
    }
};

const getViewProducts = () => {
    if (process.client) {
        // Выполняем только на клиенте
        let storedProducts = localStorage.getItem("viewedProducts");
        if (storedProducts) {
            try {
                viewedProducts.value = JSON.parse(storedProducts);
            } catch (e) {
                console.error("Error parsing viewedProducts from localStorage", e);
                localStorage.removeItem("viewedProducts"); // Очищаем некорректные данные
            }
        }
    }
    return viewedProducts.value;
};

const fbCatalog = () => {
    // Дополнительная проверка process.client на всякий случай, хотя вызывается уже внутри проверки
    if (!process.client || !currentCategoryTitle.value || !products.value || products.value.length === 0) return;

    console.log("fbCatalog triggered on client for category:", currentCategoryTitle.value); // Для отладки

    const event_id = $getEventId(); // Используем $getEventId из nuxtApp
    const external_id = localStorage.getItem("external_id");

    $fbq("track", "ViewCategory", {
        content_category: currentCategoryTitle.value,
        content_ids: products.value.map((product) => product.vendor_code),
        content_name: currentCategoryTitle.value,
        event_id: event_id,
        external_id: external_id,
        content_type: "product",
        source: "le-perle.com.ua",
    });

    if (nuxtApp.$ttq) {
        nuxtApp.$ttq.track("ViewCategory", {
            content_category: currentCategoryTitle.value,
            content_ids: products.value.map((product) => product.vendor_code),
            content_name: currentCategoryTitle.value,
            event_id: event_id,
            external_id: external_id,
            content_type: "product",
            source: "le-perle.com.ua",
        });
    }

    try {
        $fetch(apiUrl + "fbapi", {
            method: "POST",
            body: {
                action: "ViewCategory",
                event_id: event_id,
                content_category: currentCategoryTitle.value,
                content_ids: products.value.map((product) => product.id).join(","),
                content_name: currentCategoryTitle.value,
                quantity: 1, // Убедитесь, что это поле нужно
                fbc: $getCookie("_fbc"),
                fbp: $getCookie("_fbp"),
                external_id: external_id,
                useragent: window.navigator.userAgent,
                url: `${window.location.origin}${window.location.pathname}`,
            },
        });
    } catch (error) {
        console.error("Error sending FB API request:", error);
    }
};

const paginate = () => {
    let pages = [];
    const total = totalPages.value;
    const current = currentPage.value;

    if (total <= 10) {
        for (let i = 1; i <= total; i++) {
            pages.push(i);
        }
    } else {
        pages.push(1);
        let startPage = Math.max(current - 2, 2);
        let endPage = Math.min(startPage + 4, total - 1);

        if (current - 1 <= 3) {
            endPage = Math.min(6, total - 1); // Убедимся, что не выходим за границы
        } else if (total - current <= 3) {
            startPage = Math.max(total - 5, 2); // Убедимся, что не выходим за границы
            endPage = total - 1;
        }

        // Корректировка, если startPage стал больше endPage из-за граничных условий
        if (startPage > endPage) {
            if (current <= 4) {
                // Ближе к началу
                endPage = Math.min(startPage + 4, total - 1);
            } else {
                // Ближе к концу
                startPage = Math.max(endPage - 4, 2);
            }
        }

        if (startPage > 2) {
            pages.push("...");
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (endPage < total - 1) {
            pages.push("...");
        }
        pages.push(total);
    }

    return pages;
};

const updateShowVariants = (index, value) => {
    if (index >= 0 && index < showVariantsStates.value.length) {
        showVariantsStates.value[index] = value;
    }
    // Логика открытия корзины, если все варианты скрыты, требует доступа к store,
    // который не рекомендуется использовать напрямую в setup.
    // Возможно, лучше управлять этим состоянием глобально через Pinia или Vuex store.
    // if (showVariantsStates.value.every((state) => state === false)) {
    //     // this.$store.commit("openCart"); // Так не работает в setup
    // }
};

onMounted(() => {
    getViewProducts(); // Получаем просмотренные товары на клиенте
    // Вызов fbCatalog при монтировании, если данные уже есть (после SSR)
    // Это может дублировать вызов из watch, но гарантирует срабатывание после гидратации
    if (process.client && products.value.length > 0 && currentCategoryTitle.value) {
        // Проверяем, не был ли он уже вызван в watch с immediate: true
        // Простой способ - добавить флаг, но можно и без него, если дублирование не критично
        console.log("fbCatalog potentially called from onMounted");
        // fbCatalog(); // Можно раскомментировать, если watch не всегда срабатывает как ожидается
    }
});

// --- SEO Meta ---
// Используем useSeoMeta внутри setup
useSeoMeta({
    title: () => `${currentCategoryTitle.value || "Каталог"}`, // Динамический заголовок
    // titleTemplate: "%s - Le'Perle", // title уже включает суффикс
    meta: () => [
        // Используем функцию для реактивности
        {
            name: "description",
            content: `Купити ${currentCategoryTitle.value || "ювелірні вироби"} в інтернет-магазині Le'Perle. Великий вибір, доступні ціни.`,
        },
        {
            property: "og:title",
            content: `${currentCategoryTitle.value || "Каталог"} - Le'Perle`,
        },
        {
            property: "og:description",
            content: `Купити ${currentCategoryTitle.value || "ювелірні вироби"} в інтернет-магазині Le'Perle. Великий вибір, доступні ціни.`,
        },
        // Добавьте другие мета-теги по необходимости
    ],
});
</script>

<style>
/* Стили остаются без изменений */
</style>
